import {useEffect, useMemo, useState} from "react";
import {DialogPrimitive as Dialog} from "ch3-ui-lib";
import {PhoneHQLoader, PhoneHQButton, PhoneHQCheckbox} from 'ch3-ui-lib';
import {X} from "lucide-react";
import {useGenerateEnsTtsMutation} from "../../../store/features/ens/ensService";
import {
    EnsAlertTemplate,
    EnsAlertType,
    EnsLevelInputDto,
    EnsTtsRequest,
    EnsUserContactListItem,
} from "../../../store/features/ens/types";
import {Step1Type} from "../CreateAlertWizard/Step1Type";
import {Step2Target} from "../CreateAlertWizard/Step2Target";
import {Step3Escalation} from "../CreateAlertWizard/Step3Escalation";
import {
    createDefaultVoicePromptDraft,
    isVoicePromptDraftValid,
    normalizeEscalationLevels,
    normalizeVoicePromptDraft,
    levelsRequireExternalMsisdn,
    requiresVoicePrompt,
} from "../CreateAlertWizard/utils";

interface Props {
    isOpen: boolean;
    isSaving: boolean;
    presetByType: Map<EnsAlertType, EnsLevelInputDto[]>;
    smsSenderConfigured: boolean;
    template: EnsAlertTemplate | null;
    onClose: () => void;
    onSave: (template: EnsAlertTemplate) => Promise<void>;
}

const blankTemplate = (levels: readonly EnsLevelInputDto[]): EnsAlertTemplate => ({
    id: null,
    systemKey: null,
    system: false,
    type: 'CUSTOM',
    name: '',
    message: '',
    targetGroupIds: [],
    targetUserIds: [],
    targetUsers: [],
    levels: normalizeEscalationLevels(levels),
    voicePrompt: null,
    apiTriggerEnabled: false,
    sourceAlertPublicId: null,
    createdAt: null,
    updatedAt: null,
    createdByUserId: null,
    updatedByUserId: null,
});

const editableTemplate = (template: EnsAlertTemplate): EnsAlertTemplate => ({
    ...template,
    targetGroupIds: [...template.targetGroupIds],
    targetUserIds: [...template.targetUserIds],
    targetUsers: [...template.targetUsers],
    levels: normalizeEscalationLevels(template.levels),
});

const selectedUserIds = (targetUsers: readonly EnsUserContactListItem[]): number[] =>
    targetUsers.map((user) => user.userId);

export const TemplateEditorModal = ({
    isOpen,
    isSaving,
    presetByType,
    smsSenderConfigured,
    template,
    onClose,
    onSave,
}: Props) => {
    const defaultLevels = useMemo(
        () => normalizeEscalationLevels(presetByType.get('CUSTOM') ?? []),
        [presetByType],
    );
    const [draft, setDraft] = useState<EnsAlertTemplate | null>(null);
    const [voicePromptDraft, setVoicePromptDraft] = useState<EnsTtsRequest>(createDefaultVoicePromptDraft);
    const [voicePromptDraftDirty, setVoicePromptDraftDirty] = useState(false);
    const [generateVoicePrompt, {isLoading: isGeneratingVoicePrompt}] = useGenerateEnsTtsMutation();

    useEffect(() => {
        if (!isOpen) {
            setDraft(null);
            setVoicePromptDraft(createDefaultVoicePromptDraft());
            setVoicePromptDraftDirty(false);
            return;
        }

        setDraft(template ? editableTemplate(template) : blankTemplate(defaultLevels));
        setVoicePromptDraft(createDefaultVoicePromptDraft());
        setVoicePromptDraftDirty(false);
    }, [defaultLevels, isOpen, template]);

    const generatedVoicePromptRequired = draft != null
        && draft.apiTriggerEnabled
        && requiresVoicePrompt(draft.levels)
        && draft.voicePrompt == null;
    const voicePromptDraftRequired = generatedVoicePromptRequired || voicePromptDraftDirty;
    const saveDisabled = !draft
        || draft.name.trim().length === 0
        || (voicePromptDraftRequired && !isVoicePromptDraftValid(voicePromptDraft))
        || draft.levels.length === 0
        || draft.levels.some((level) => level.delaySecondsFromPrevious < 0)
        || isSaving
        || isGeneratingVoicePrompt;
    const isEdit = template?.id != null;

    const updateTargets = (patch: Partial<{
        name: string;
        message: string;
        targetGroupIds: number[];
        targetUsers: EnsUserContactListItem[];
    }>) => {
        setDraft((previous) => {
            if (!previous) return previous;

            const targetUsers = patch.targetUsers ?? previous.targetUsers;
            return {
                ...previous,
                ...patch,
                targetUsers,
                targetUserIds: selectedUserIds(targetUsers),
            };
        });
    };

    const handleTypeChange = (type: EnsAlertType) => {
        setDraft((previous) => {
            if (!previous) return previous;

            return {
                ...previous,
                type,
                levels: normalizeEscalationLevels(presetByType.get(type) ?? previous.levels),
            };
        });
    };

    const handleSave = async () => {
        if (!draft || saveDisabled) return;
        let templateToSave = draft;
        const shouldGenerateVoicePrompt = templateToSave.voicePrompt == null
            && (voicePromptDraftDirty
                || (templateToSave.apiTriggerEnabled && requiresVoicePrompt(templateToSave.levels)));
        if (shouldGenerateVoicePrompt) {
            try {
                const voicePrompt = await generateVoicePrompt(
                    normalizeVoicePromptDraft(voicePromptDraft),
                ).unwrap();
                templateToSave = {...templateToSave, voicePrompt};
                setDraft(templateToSave);
            } catch {
                // toast handled in service; keep the editor open so the user can retry
                return;
            }
        }
        await onSave(templateToSave);
    };

    return (
        <Dialog.Root open={isOpen} onOpenChange={(open) => (
            !open && !isSaving && !isGeneratingVoicePrompt ? onClose() : undefined
        )}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-50 bg-scrim data-[state=open]:animate-overlayShow"/>
                <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] w-[880px] max-w-[calc(100vw-48px)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl bg-surface shadow-[0_20px_60px_rgba(0,0,0,0.18)]">
                    <div className="flex items-center justify-between gap-4 border-b border-line px-5 py-4">
                        <div className="min-w-0">
                            <Dialog.Title className="text-[15px] font-bold text-ink">
                                {isEdit ? 'Edit template' : 'New template'}
                            </Dialog.Title>
                            <div className="mt-0.5 text-xs text-ink-muted">
                                Save a reusable alert setup for future emergency notifications.
                            </div>
                        </div>
                        <button
                            type="button"
                            aria-label="Close"
                            onClick={onClose}
                            disabled={isSaving || isGeneratingVoicePrompt}
                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-surface-muted text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <X size={15} className="!p-0"/>
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto px-5 py-4">
                        {!draft ? (
                            <div className="flex items-center justify-center py-10">
                                <PhoneHQLoader/>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-5">
                                <Step1Type
                                    type={draft.type}
                                    presetByType={presetByType}
                                    onChange={handleTypeChange}
                                />

                                <div className="border-t border-line pt-5">
                                    <Step2Target
                                        name={draft.name}
                                        message={draft.message}
                                        targetGroupIds={draft.targetGroupIds}
                                        targetUsers={draft.targetUsers}
                                        voicePrompt={draft.voicePrompt}
                                        voicePromptDraft={voicePromptDraft}
                                        voicePromptRequired={draft.apiTriggerEnabled && requiresVoicePrompt(draft.levels)}
                                        showVoicePrompt={requiresVoicePrompt(draft.levels)}
                                        externalNumberRequired={levelsRequireExternalMsisdn(draft.levels)}
                                        onChange={updateTargets}
                                        onVoicePromptChange={(voicePrompt) => {
                                            setDraft((previous) => previous ? {...previous, voicePrompt} : previous);
                                            if (voicePrompt == null) setVoicePromptDraftDirty(true);
                                        }}
                                        onVoicePromptDraftChange={(nextVoicePromptDraft) => {
                                            setVoicePromptDraft(nextVoicePromptDraft);
                                            setVoicePromptDraftDirty(true);
                                            setDraft((previous) => previous ? {...previous, voicePrompt: null} : previous);
                                        }}
                                    />
                                </div>

                                <div className="border-t border-line pt-5">
                                    <Step3Escalation
                                        levels={draft.levels}
                                        onChange={(levels) =>
                                            setDraft((previous) => previous
                                                ? {...previous, levels: normalizeEscalationLevels(levels)}
                                                : previous
                                            )
                                        }
                                        voicePromptSet={draft.voicePrompt != null}
                                        smsSenderConfigured={smsSenderConfigured}
                                        enforceVoicePrompt={draft.apiTriggerEnabled}
                                    />
                                </div>

                                <div className="border-t border-line pt-5">
                                    <div className="flex items-start gap-3">
                                        <PhoneHQCheckbox
                                            checked={draft.apiTriggerEnabled}
                                            onCheckedChange={(checked) => setDraft((previous) => previous
                                                ? {...previous, apiTriggerEnabled: checked === true}
                                                : previous
                                            )}
                                        />
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-ink">
                                                Allow external systems to trigger this template
                                            </div>
                                            <div className="mt-1 text-xs leading-5 text-ink-muted">
                                                This change is applied only when you save the complete template. API keys are managed separately.
                                            </div>
                                            {draft.apiTriggerEnabled && (
                                                <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">
                                                    Every active tenant API key can trigger every API-enabled template and other API_CLIENT endpoints. A key name is not an authorization boundary.
                                                </div>
                                            )}
                                            {template?.apiTriggerEnabled === true && !draft.apiTriggerEnabled && (
                                                <div className="mt-2 rounded-lg border border-line bg-surface-muted px-3 py-2 text-xs leading-5 text-ink-secondary">
                                                    Saving will prevent new API triggers. Alerts already created will continue, and no API key will be disabled or deleted.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3">
                        <PhoneHQButton
                            variant="secondary"
                            size="sm"
                            onClick={onClose}
                            disabled={isSaving || isGeneratingVoicePrompt}
                        >
                            Cancel
                        </PhoneHQButton>
                        <PhoneHQButton
                            variant="primary"
                            size="sm"
                            onClick={handleSave}
                            disabled={saveDisabled}
                        >
                            {isGeneratingVoicePrompt
                                ? 'Generating voice…'
                                : isSaving
                                    ? 'Saving…'
                                    : (isEdit ? 'Save changes' : 'Create template')}
                        </PhoneHQButton>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};
